import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { behaviourApi, guardrailApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import GaugeMeter from "@/components/shared/GaugeMeter";
import { AreaChart, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { BrainCircuit, MessageSquare, TrendingUp, TrendingDown, Minus, ShieldAlert, Award, AlertTriangle, Info, Lightbulb } from "lucide-react";

const METRIC_CONFIG = {
  CUSTODY_DISCIPLINE_INDEX: { label: "Custody Discipline", invert: false, desc: "Adherence to custody rules" },
  OVERRIDE_RELIANCE_INDEX: { label: "Override Reliance", invert: true, desc: "Lower is better" },
  FINANCIAL_STRESS_REACTIVITY: { label: "Stress Reactivity", invert: true, desc: "Lower is better" },
  PLANNING_CONSISTENCY_RATIO: { label: "Planning Consistency", invert: false, desc: "Planned vs actual payments" },
  STRATEGIC_MATURITY_INDEX: { label: "Strategic Maturity", invert: false, desc: "Overall governance maturity" },
};

const MSG_ICONS = { PRAISE: Award, WARNING: AlertTriangle, COACHING: Lightbulb, INFO: Info, ALERT: ShieldAlert };
const MSG_COLORS = {
  PRAISE: "border-emerald-500/20 bg-emerald-500/5",
  WARNING: "border-amber-500/20 bg-amber-500/5",
  COACHING: "border-cyan-500/20 bg-cyan-500/5",
  INFO: "border-blue-500/20 bg-blue-500/5",
  ALERT: "border-red-500/20 bg-red-500/5",
};
const MSG_TEXT_COLORS = { PRAISE: "text-emerald-400", WARNING: "text-amber-400", COACHING: "text-cyan-400", INFO: "text-blue-400", ALERT: "text-red-400" };

export default function InsightsPage() {
  const { formatMoney } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [states, setStates] = useState([]);
  const [coaching, setCoaching] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      behaviourApi.metrics(),
      behaviourApi.state(),
      behaviourApi.coachingFeed(),
      guardrailApi.listOverrides(),
    ]).then(([m, s, c, o]) => {
      setMetrics(m.data);
      setStates(s.data);
      setCoaching(c.data);
      setOverrides(o.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Current state
  const latestState = states.length > 0 ? states[states.length - 1] : null;
  const currentState = latestState?.collapsed_state || "STRATEGIC";

  // Chart data
  const chartData = states.map(s => ({
    date: new Date(s.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    Survival: +(s.state_probs?.SURVIVAL * 100 || 0).toFixed(1),
    Emotional: +(s.state_probs?.EMOTIONAL * 100 || 0).toFixed(1),
    Strategic: +(s.state_probs?.STRATEGIC * 100 || 0).toFixed(1),
  }));

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div>
        <h1 className="font-barlow font-bold text-2xl text-slate-50">Behavioural Intelligence</h1>
        <p className="text-sm text-slate-500 mt-0.5">Financial conduct analysis, coaching, and triune-state governance</p>
      </div>

      {/* Current State Banner */}
      <div className={`p-4 rounded-xl border flex items-center justify-between ${
        currentState === "STRATEGIC" ? "border-emerald-500/30 bg-emerald-500/5" :
        currentState === "EMOTIONAL" ? "border-amber-500/30 bg-amber-500/5" :
        "border-red-500/30 bg-red-500/5"
      }`} data-testid="current-state-banner">
        <div className="flex items-center gap-3">
          <BrainCircuit className={`w-6 h-6 ${
            currentState === "STRATEGIC" ? "text-emerald-400" : currentState === "EMOTIONAL" ? "text-amber-400" : "text-red-400"
          }`} />
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Current Behavioural State</div>
            <div className={`font-barlow font-bold text-xl ${
              currentState === "STRATEGIC" ? "text-emerald-400" : currentState === "EMOTIONAL" ? "text-amber-400" : "text-red-400"
            }`}>
              {currentState}
            </div>
          </div>
        </div>
        {latestState && (
          <div className="flex gap-4">
            {Object.entries(latestState.state_probs || {}).map(([key, val]) => (
              <div key={key} className="text-center">
                <div className="text-[10px] text-slate-500 uppercase">{key}</div>
                <div className="text-sm font-mono tabular text-slate-300">{(val * 100).toFixed(1)}%</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Tabs defaultValue="metrics" className="space-y-4">
        <TabsList className="bg-slate-900/50 border border-slate-800/50">
          <TabsTrigger value="metrics" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-amber-400">Metrics</TabsTrigger>
          <TabsTrigger value="timeline" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-amber-400">State Timeline</TabsTrigger>
          <TabsTrigger value="coaching" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-amber-400">Coaching Feed</TabsTrigger>
          <TabsTrigger value="overrides" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-amber-400">Override Log</TabsTrigger>
        </TabsList>

        {/* Metrics Tab */}
        <TabsContent value="metrics">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {metrics.map((m) => {
              const config = METRIC_CONFIG[m.metric] || { label: m.metric, invert: false, desc: "" };
              const trend = m.details?.trend;
              const prevScore = m.details?.prev_score;
              const diff = prevScore ? (m.score - prevScore).toFixed(1) : null;

              return (
                <Card key={m.id} className="bg-slate-900/40 border-slate-800/50" data-testid={`metric-card-${m.metric.toLowerCase()}`}>
                  <CardContent className="p-4 flex flex-col items-center">
                    <GaugeMeter score={m.score} label={config.label} size={100} invert={config.invert} />
                    <div className="mt-2 flex items-center gap-1">
                      {trend === "up" ? <TrendingUp className="w-3 h-3 text-emerald-400" /> :
                       trend === "down" ? <TrendingDown className="w-3 h-3 text-red-400" /> :
                       <Minus className="w-3 h-3 text-slate-500" />}
                      {diff && (
                        <span className={`text-[10px] font-mono ${
                          (config.invert ? parseFloat(diff) < 0 : parseFloat(diff) > 0) ? "text-emerald-400" : "text-red-400"
                        }`}>
                          {parseFloat(diff) > 0 ? "+" : ""}{diff}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card className="bg-slate-900/40 border-slate-800/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Triune-State Probability Timeline (30 Days)</CardTitle>
            </CardHeader>
            <CardContent data-testid="state-timeline-chart">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gradSurvival" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradEmotional" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradStrategic" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: 'rgba(10,16,32,0.97)', border: '1px solid rgba(120,180,255,0.25)', borderRadius: '12px', padding: '10px 14px', color: '#fff' }} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="Survival" stroke="#EF4444" fill="url(#gradSurvival)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Emotional" stroke="#F59E0B" fill="url(#gradEmotional)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Strategic" stroke="#10B981" fill="url(#gradStrategic)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coaching Tab */}
        <TabsContent value="coaching">
          <div className="space-y-3">
            {coaching.map((msg) => {
              const Icon = MSG_ICONS[msg.message_type] || Info;
              return (
                <Card key={msg.id} className={`border ${MSG_COLORS[msg.message_type]} bg-slate-900/40`} data-testid={`coaching-msg-${msg.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        <Icon className={`w-4 h-4 ${MSG_TEXT_COLORS[msg.message_type]}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-[9px] ${MSG_COLORS[msg.message_type]} ${MSG_TEXT_COLORS[msg.message_type]} border-none`}>
                            {msg.message_type}
                          </Badge>
                          <span className="text-[10px] text-slate-600">{formatDate(msg.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-300 leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Overrides Tab */}
        <TabsContent value="overrides">
          <Card className="bg-slate-900/40 border-slate-800/50">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800/50 hover:bg-transparent">
                    <TableHead className="text-[10px] text-slate-500">Date</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Action</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Vault</TableHead>
                    <TableHead className="text-[10px] text-slate-500 text-right">Amount</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Status</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrides.map((ovr) => (
                    <TableRow key={ovr.id} className="border-slate-800/30" data-testid={`override-row-${ovr.id}`}>
                      <TableCell className="text-xs text-slate-400 font-mono">{formatDate(ovr.created_at)}</TableCell>
                      <TableCell className="text-xs text-slate-300">{ovr.attempted_action}</TableCell>
                      <TableCell className="text-xs text-slate-400 font-mono">{ovr.vault_id?.replace('vault-', '')}</TableCell>
                      <TableCell className="text-right text-xs font-mono tabular text-slate-200">{formatMoney(ovr.amount)}</TableCell>
                      <TableCell>
                        <Badge className={`text-[9px] ${
                          ovr.status === "APPROVED" ? "bg-emerald-500/15 text-emerald-400" :
                          ovr.status === "DENIED" ? "bg-red-500/15 text-red-400" :
                          "bg-amber-500/15 text-amber-400"
                        } border-none`}>
                          {ovr.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{ovr.reason_text}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
